import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, ElementRef, ViewChild, ChangeDetectorRef, NgZone } from "@angular/core";
import { environment } from '@environments/environment';
import { CommonModule, DatePipe } from "@angular/common";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Subject, firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

import { FirebaseStorageService } from "../../../../../core/services/api/firebase-storage.service";
import { ApiService, Region, Country } from "@core/services/api/api.service";
import { ManageAssessmentApiService, ClassificationsStatus, DeviceClassifications, ProductDesignHistory, ChangeTypes, ProductFamilies, ManageAssessmentRequestPayload, AttachmentData } from "@core/services/api/manage-assessment-api.service";

import { PrimeNGSharedModule } from "@shared/prime/prime-ng.module";
import { MessageService } from "primeng/api";
import { CalendarModule } from 'primeng/calendar';
import { MultiSelectModule } from 'primeng/multiselect';
import { EditorModule } from 'primeng/editor';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface UploadedFile {
  name: string;
  url: string;
  path?: string;
  id?: number;
  status?: string;
  uploading?: boolean;
  uploaded?: boolean;
  progress?: number;
  error?: string;
  bucket?: string;        // storage bucket name
  localBlobUrl?: string;  // temporary blob before real URL
  rawFile?: File;         // original File reference
}

@Component({
  selector: "app-manage-assessment-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNGSharedModule,
    CalendarModule,
    MultiSelectModule,
    EditorModule,
  ],
  templateUrl: "./manage-assessment-form.component.html",
  styleUrls: ["./manage-assessment-form.component.scss"],
  providers: [MessageService, DatePipe],
})
export class ManageAssessmentFormComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() visible: boolean = false;
  @Input() rcaDataToPatch: any = null;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeDialog = new EventEmitter<void>();
  @Output() rcaCreated = new EventEmitter<any>();
  @Output() rcaPatched = new EventEmitter<void>();

  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

  rcaForm!: FormGroup;
  formSubmitted: boolean = false;

  countries: Country[] = [];
  regions: Region[] = [];
  allCountries: Country[] = [];
  usMedDeviceOptions: DeviceClassifications[] = [];
  usClassifications: ClassificationsStatus[] = [];
  euMedDeviceOptions: DeviceClassifications[] = [];
  typeOfChanges: ChangeTypes[] = [];
  euClassifications: ClassificationsStatus[] = [];
  productFamilies: ProductFamilies[] = [];
  productDesignHistoryOptions: ProductDesignHistory[] = [];

  uploadedFile: UploadedFile | null = null;
  uploadedFiles: UploadedFile[] = []; // multi-file support
  uploading: boolean = false;
  uploadError: string = "";
  // Keep a reference to the raw File so user can download immediately (even before upload completes)
  private rawSelectedFile: File | null = null;

  private uploadedFilePath: string | null = null;
  private uploadedFileBucket: string | null = null;

  private unsubscribe$ = new Subject<void>();

  // Track whether "Other(s)" is selected in Product Design History Documentation
  showOtherDesignDocsField: boolean = false;

  // Minimum date for Due Date (one day after issued date)
  dueDateMinDate: Date | null = null;

  /** Full-screen p-editor for change description (same toolbar/modules as inline editor) */
  changeDescriptionMaxPreview = false;

  readonly changeDescriptionMaxEditorStyle = { height: "calc(100vh - 140px)" };

  readonly changeDescriptionMaxDialogContentStyle: Record<string, string | number> = {
    overflow: "hidden",
    display: "flex",
    "flex-direction": "column",
    flex: "1 1 auto",
    "min-height": 0,
  };

  // Quill editor configuration
  editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  // Define field labels and descriptions here for easy mapping
  private fieldMetadata = {
    title: {
      label: "Title",
      description: "Title for the assessment.",
    },
    region: {
      label: "Region",
      description: "Geographical region for the assessment.",
    },
    usMedDeviceClass: {
      label: "US Med Device Class",
      description: "US Medical Device Classification.",
    },
    productDesignHistoryDocumentation: {
      label: "Product Design History Documentation",
      description: "Relevant product design history documents.",
    },
    country: {
      label: "Country",
      description: "Countries impacted by the change.",
    },
    usClassification: {
      label: "US Classification",
      description: "US Classification status.",
    },
    changeDescription: {
      label: "Change Description",
      description: "Detailed description of the regulatory change.",
    },
    ecrnEcnNumber: {
      label: "ERN/ECN Number",
      description: "Optional ECRN or ECN number.",
    },
    euMedDeviceClass: {
      label: "EU Med Device Class",
      description: "EU Medical Device Classification.",
    },
    plannedTimeframeForImplementation: {
      label: "Planned Timeline",
      description: "Estimated timeline for implementation.",
    },
    typeOfChange: {
      label: "Type of Change",
      description: "Categories describing the nature of the change.",
    },
    euClassification: {
      label: "EU Classification",
      description: "EU Classification status.",
    },
    additionalAttachmentsDescription: {
      label: "Additional Attachments Description",
      description: "Description of any additional attached files.",
    },
    productFamily: {
      label: "Product Family",
      description: "Product families affected by the change.",
    },
    issuedDate: {
      label: "Issued Date",
      description: "Date the assessment was issued.",
    },
    catalogueNumber: {
      label: "Catalogue Number",
      description: "Product catalogue number.",
    },
    dueDate: {
      label: "Due Date",
      description: "Date the assessment is due."
    },
    otherDesignDocs: {
      label: "Other Design Documents",
      description: "Custom validation documentation and internal test reports.",
    },
  };

  constructor(
    private fb: FormBuilder,
    private manageAssessmentApiService: ManageAssessmentApiService,
    private apiService: ApiService,
    private messageService: MessageService,
    private datePipe: DatePipe,
    private firebaseStorageService: FirebaseStorageService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadDropdownData();
    this.setupDependentDropdowns();
    this.setupConditionalValidation(); // Call the new method here
    this.setupDateValidation();
  }

  setupDependentDropdowns(): void {
    const responsibleRegionControl = this.rcaForm.get("region");
    const responsibleCountryControl = this.rcaForm.get("country");

    responsibleRegionControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedRegions: Region[] | null) => {
        responsibleCountryControl?.reset([]); // Reset countries when regions change
        this.countries = []; // Clear current countries
        if (selectedRegions && selectedRegions.length > 0) {
          // Filter countries based on ALL selected regions
          const selectedRegionIds = selectedRegions.map((region) => region.id);
          this.countries = this.allCountries.filter((country) =>
            selectedRegionIds.includes(country.region.id)
          );
          responsibleCountryControl?.enable();
        } else {
          // If no regions are selected, disable country and clear its options
          responsibleCountryControl?.disable();
          this.countries = [];
        }
        this.cdr.detectChanges();
      });
  }

  // Setup date-based validation (minDate for Due Date based on Issued Date)
  setupDateValidation(): void {
    const issuedDateControl = this.rcaForm.get('issuedDate');

    issuedDateControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((issuedDate: Date | null) => {
        if (issuedDate) {
          const minDate = new Date(issuedDate);
          minDate.setDate(minDate.getDate() + 1);
          this.dueDateMinDate = minDate;
        } else {
          this.dueDateMinDate = null;
        }
        this.cdr.detectChanges();
      });
  }

  // New method for conditional validation
  setupConditionalValidation(): void {
    const usClassificationControl = this.rcaForm.get('usClassification');
    const usMedDeviceClassControl = this.rcaForm.get('usMedDeviceClass');

    const euClassificationControl = this.rcaForm.get('euClassification');
    const euMedDeviceClassControl = this.rcaForm.get('euMedDeviceClass');

    usClassificationControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((usClassification: ClassificationsStatus | null) => {
        if (usClassification?.name === 'Yes') {
          usMedDeviceClassControl?.setValidators(Validators.required);
          usMedDeviceClassControl?.enable();
        } else if (usClassification?.name === 'No') {
          // Disable and clear when "No" is selected
          usMedDeviceClassControl?.clearValidators();
          usMedDeviceClassControl?.setValue(null);
          usMedDeviceClassControl?.disable();
        } else {
          // For "N/A" or null, remove validators but keep enabled
          usMedDeviceClassControl?.clearValidators();
          usMedDeviceClassControl?.enable();
        }
        usMedDeviceClassControl?.updateValueAndValidity();
        this.cdr.detectChanges();
      });

    euClassificationControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((euClassification: ClassificationsStatus | null) => {
        if (euClassification?.name === 'Yes') {
          euMedDeviceClassControl?.setValidators(Validators.required);
          euMedDeviceClassControl?.enable();
        } else if (euClassification?.name === 'No') {
          // Disable and clear when "No" is selected
          euMedDeviceClassControl?.clearValidators();
          euMedDeviceClassControl?.setValue(null);
          euMedDeviceClassControl?.disable();
        } else {
          // For "N/A" or null, remove validators but keep enabled
          euMedDeviceClassControl?.clearValidators();
          euMedDeviceClassControl?.enable();
        }
        euMedDeviceClassControl?.updateValueAndValidity();
        this.cdr.detectChanges();
      });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["visible"] &&
      changes["visible"].currentValue === true &&
      this.rcaDataToPatch &&
      Object.keys(this.rcaDataToPatch).length > 0
    ) {
      Promise.all([
        this.apiService.getRegions().toPromise(),
        this.apiService.getCountries().toPromise(),
        this.manageAssessmentApiService.getClassifications().toPromise(),
        this.manageAssessmentApiService
          .getUsDeviceClassifications()
          .toPromise(),
        this.manageAssessmentApiService
          .getEuDeviceClassifications()
          .toPromise(),
        this.manageAssessmentApiService
          .getProductDesignHistoryDoc()
          .toPromise(),
        this.manageAssessmentApiService.getChangeTypes().toPromise(),
        this.manageAssessmentApiService.getProductFamilies().toPromise(),
      ])
        .then(
          ([
            regionsResponse,
            countriesResponse,
            classificationsResponse,
            usDeviceClassificationsResponse,
            euDeviceClassificationsResponse,
            productDesignHistoryResponse,
            changeTypesResponse,
            productFamiliesResponse,
          ]) => {
            this.regions = regionsResponse?.regions || [];
            this.allCountries = countriesResponse?.countries || [];
            this.usClassifications =
              classificationsResponse?.classifications_status || [];
            this.euClassifications =
              classificationsResponse?.classifications_status || [];
            this.usMedDeviceOptions =
              usDeviceClassificationsResponse?.us_device_classifications || [];
            this.euMedDeviceOptions =
              euDeviceClassificationsResponse?.eu_device_classifications || [];
            this.productDesignHistoryOptions =
              productDesignHistoryResponse?.product_design_history_docs || [];
            this.typeOfChanges = changeTypesResponse?.change_types || [];
            this.productFamilies =
              productFamiliesResponse?.product_families || [];
            // Now that all dropdown data is loaded, patch the form
            this.getAssessmentByRca(this.rcaDataToPatch?.row);
          }
        )
        .catch((error) => {
          console.error(
            "Failed to load all dropdown data for patching:",
            error
          );
          this.messageService.add({
            severity: "error",
            summary: "Data Load Error",
            detail: "Failed to load necessary data for form. Please try again.",
            life: 5000,
          });
        });
    } else if (
      changes["visible"] &&
      changes["visible"].currentValue === false
    ) {
      this.resetForm();
    }
  }

  loadDropdownData(): void {
    this.apiService
      .getRegions()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { regions: Region[] }) =>
          (this.regions = response.regions),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load regions", err),
      });

    this.apiService
      .getCountries()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { countries: Country[] }) => {
          this.allCountries = response.countries;
        },
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load countries", err),
      });

    this.manageAssessmentApiService
      .getClassifications()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { classifications_status: ClassificationsStatus[] }) =>
        (this.usClassifications = this.euClassifications =
          response.classifications_status),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load US/EU classifications", err),
      });

    this.manageAssessmentApiService
      .getUsDeviceClassifications()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: {
          us_device_classifications: DeviceClassifications[];
        }) => (this.usMedDeviceOptions = response.us_device_classifications),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load US device classifications", err),
      });

    this.manageAssessmentApiService
      .getEuDeviceClassifications()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: {
          eu_device_classifications: DeviceClassifications[];
        }) => (this.euMedDeviceOptions = response.eu_device_classifications),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load EU device classifications", err),
      });

    this.manageAssessmentApiService
      .getProductDesignHistoryDoc()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: {
          product_design_history_docs: ProductDesignHistory[];
        }) =>
        (this.productDesignHistoryOptions =
          response.product_design_history_docs),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load product design history documents", err),
      });

    this.manageAssessmentApiService
      .getChangeTypes()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { change_types: ChangeTypes[] }) =>
          (this.typeOfChanges = response.change_types),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load type of changes", err),
      });

    this.manageAssessmentApiService
      .getProductFamilies()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { product_families: ProductFamilies[] }) =>
          (this.productFamilies = response.product_families),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load product families", err),
      });
  }

  initializeForm(): void {
    this.rcaForm = this.fb.group({
      title: ["",Validators.required],
      region: [[], Validators.required],
      // Make these initially optional, validation will be added conditionally
      usMedDeviceClass: [null],
      productDesignHistoryDocumentation: [[], Validators.required],
      otherDesignDocs: [""], // New field for "Other Design Docs"
      country: [[], Validators.required],
      usClassification: [null, Validators.required],
      changeDescription: ["", Validators.required],
      ecrnEcnNumber: [""], // Optional field
      // Make these initially optional, validation will be added conditionally
      euMedDeviceClass: [null],
      plannedTimeframeForImplementation: [null, Validators.required],
      typeOfChange: [[], Validators.required],
      euClassification: [null, Validators.required],
      additionalAttachmentsDescription: [""],
      productFamily: [[], Validators.required],
      issuedDate: [null, Validators.required],
      catalogueNumber: ["", Validators.required],
      dueDate: [null, Validators.required],
    });

    // Set up tracking for "Other(s)" selection
    this.setupOtherDesignDocsTracking();
  }

  setupOtherDesignDocsTracking(): void {
    const productDesignHistoryControl = this.rcaForm.get('productDesignHistoryDocumentation');
    const otherDesignDocsControl = this.rcaForm.get('otherDesignDocs');

    productDesignHistoryControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedOptions: ProductDesignHistory[] | null) => {
        // Check if "Other(s)" is selected
        const hasOtherSelected = selectedOptions?.some(option =>
          option.name?.toLowerCase().includes('other')
        ) || false;

        this.showOtherDesignDocsField = hasOtherSelected;

        // Add/remove validation based on whether "Other(s)" is selected
        if (hasOtherSelected) {
          otherDesignDocsControl?.setValidators([Validators.required]);
        } else {
          otherDesignDocsControl?.clearValidators();
          // Only clear the field value if there's no existing value from API
          if (!otherDesignDocsControl?.value) {
            otherDesignDocsControl?.setValue('');
          }
        }
        otherDesignDocsControl?.updateValueAndValidity();
      });

    // Trigger initial check in case form already has values
    const currentSelection = productDesignHistoryControl?.value;
    if (currentSelection) {
      const hasOtherSelected = currentSelection.some((option: ProductDesignHistory) =>
        option.name?.toLowerCase().includes('other')
      );
      this.showOtherDesignDocsField = hasOtherSelected;

      if (hasOtherSelected) {
        otherDesignDocsControl?.setValidators([Validators.required]);
      } else {
        otherDesignDocsControl?.clearValidators();
      }
      otherDesignDocsControl?.updateValueAndValidity();
    }
  }

  getAssessmentByRca(rca: any): void {
    console.log('getAssessmentByRca called with:', rca);
    this.manageAssessmentApiService
      .getManageAssessmentByRca(rca.rca_id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          console.log('API response received:', response);
          this.patchFormWithRcaData(response);
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to fetch RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to fetch RCA. Please try again.",
            life: 5000,
          });
        },
      });
  }

  // This method now receives the full RCA data from the API response
  patchFormWithRcaData(rca: any) {
    if (!rca) {
      console.warn("No RCA data provided for patching.");
      return;
    }
    const findById = (arr: any[], id: number | string) =>
      arr.find((item) => item.id === id);
    const findByName = (arr: any[], name: string) => {
      if (!name) return null;
      // Try exact match first
      let found = arr.find((item) => item.name === name);
      // If not found, try case-insensitive match
      if (!found) {
        found = arr.find((item) =>
          item.name?.toLowerCase() === name.toLowerCase()
        );
      }
      return found;
    };

    // Map API values to dropdown values
    // The API returns "Unknown" but we need to determine if it should be "Yes", "No", or "N/A"
    // Logic: If a device class is specified, the classification should be "Yes"
    //        Otherwise, keep it null (empty) if no value is provided
    const mapClassificationStatus = (status: string | null, hasDeviceClass: boolean): string | null => {
      if (!status) return null;

      // If the status is "Unknown" and there's a device class, it means "Yes"
      if (status.toLowerCase() === 'unknown' && hasDeviceClass) {
        return 'Yes';
      }

      // If the status is "Unknown" and no device class, return null (empty)
      if (status.toLowerCase() === 'unknown' && !hasDeviceClass) {
        return null;
      }

      return status;
    };

    // Check if device classes exist to determine the classification mapping
    const hasUsMedDeviceClass = !!rca.us_med_class_id;
    const hasEuMedDeviceClass = !!rca.eu_med_class_id;

    // For classifications, the API returns the name as a string, so we need to find by name
    const mappedUsClassStatus = mapClassificationStatus(rca.us_class_status, hasUsMedDeviceClass);
    const mappedEuClassStatus = mapClassificationStatus(rca.eu_class_status, hasEuMedDeviceClass);

    const usClassificationValue = mappedUsClassStatus
      ? findByName(this.usClassifications, mappedUsClassStatus)
      : null;
    const euClassificationValue = mappedEuClassStatus
      ? findByName(this.euClassifications, mappedEuClassStatus)
      : null;

    console.log('Patching classifications:', {
      usClassStatus: rca.us_class_status,
      hasUsMedDeviceClass,
      mappedUsClassStatus,
      usClassificationValue,
      euClassStatus: rca.eu_class_status,
      hasEuMedDeviceClass,
      mappedEuClassStatus,
      euClassificationValue,
      availableUsClassifications: this.usClassifications,
      availableEuClassifications: this.euClassifications,
      usClassificationNames: this.usClassifications.map(c => c.name),
      euClassificationNames: this.euClassifications.map(c => c.name)
    });

    this.rcaForm.patchValue({
      title: rca.title || "",
      usClassification: usClassificationValue,
      euClassification: euClassificationValue,
      ecrnEcnNumber: rca.ecn_ern,
      catalogueNumber: rca.catalogue_no,
      changeDescription: rca.description,
      plannedTimeframeForImplementation: rca.planned_timeline,
      issuedDate: rca.issue_date ? new Date(rca.issue_date) : null,
      dueDate: rca.due_date ? new Date(rca.due_date) : null,
      additionalAttachmentsDescription: rca.attachment_description || "",
    });

    // Manually patch US Med Device Class and EU Med Device Class AFTER classifications are set
    // This ensures conditional validation is applied correctly based on the patched classification
    // For device classes, the API also returns the name as a string
    const usMedDeviceClassValue = rca.us_med_class_id
      ? findByName(this.usMedDeviceOptions, rca.us_med_class_id)
      : null;
    const euMedDeviceClassValue = rca.eu_med_class_id
      ? findByName(this.euMedDeviceOptions, rca.eu_med_class_id)
      : null;

    console.log('Patching device classes:', {
      usMedClassId: rca.us_med_class_id,
      usMedDeviceClassValue,
      euMedClassId: rca.eu_med_class_id,
      euMedDeviceClassValue,
      availableUsMedDeviceOptions: this.usMedDeviceOptions,
      availableEuMedDeviceOptions: this.euMedDeviceOptions
    });

    this.rcaForm.patchValue({
      usMedDeviceClass: usMedDeviceClassValue,
      euMedDeviceClass: euMedDeviceClassValue,
    });

    // After patching classification values, explicitly trigger updateValueAndValidity
    // for the dependent fields to ensure their validation status is correct.
    this.rcaForm.get('usClassification')?.updateValueAndValidity();
    this.rcaForm.get('usMedDeviceClass')?.updateValueAndValidity();
    this.rcaForm.get('euClassification')?.updateValueAndValidity();
    this.rcaForm.get('euMedDeviceClass')?.updateValueAndValidity();

    // Manually trigger enable/disable logic based on patched classification values
    if (usClassificationValue?.name === 'No') {
      this.rcaForm.get('usMedDeviceClass')?.disable();
    } else {
      this.rcaForm.get('usMedDeviceClass')?.enable();
    }

    if (euClassificationValue?.name === 'No') {
      this.rcaForm.get('euMedDeviceClass')?.disable();
    } else {
      this.rcaForm.get('euMedDeviceClass')?.enable();
    }


    // Handle attachment data for display and deletion
    // Assuming rca.attachments is an array, and we only care about the first one for now
    if (rca.attachments && rca.attachments.length > 0) {
      // const firstAttachment = rca.attachments[0]; // Get the first attachment object

      // Reset current uploaded file state
      this.uploadedFiles = [];
      this.uploadedFilePath = null;
      this.uploadedFileBucket = null;

      for (const attachment of rca.attachments) {
        let uploadedFile: any = null;
        if (attachment.url) {
          // This is a Firebase-stored attachment with a direct URL
          const displayUrl = attachment.url;
          // Prefer file_name from API response, otherwise extract filename from URL
          const fileName =
            attachment.file_name ||
            displayUrl.substring(displayUrl.lastIndexOf("/") + 1);

          // Extract path and bucket from the URL for internal state management
          const filePath = this.extractPathFromUrl(displayUrl);
          const bucketName = this.extractBucketFromUrl(displayUrl);

          uploadedFile = {
            name: fileName,
            url: displayUrl,
            path: filePath || undefined, // Store the actual Firebase path
            id: attachment.id, // Store ID if available
            status: attachment.status, // Store status if available
            localBlobUrl: displayUrl, // Use the actual URL for immediate download
          };
          this.uploadedFilePath = filePath; // Store the path for deletion
          this.uploadedFileBucket = bucketName; // Store the bucket for payload construction
        } else if (attachment.id) {
          // If only ID is present (and no URL), it's an existing attachment identified by ID
          // Use the actual filename if available, otherwise fall back to a placeholder
          const fileName =
            attachment.file_name ||
            attachment.name ||
            `Attachment ID: ${attachment.id}`;
          uploadedFile = {
            name: fileName,
            url: "", // No direct URL for display
            id: attachment.id,
            status: attachment.status, // Store status if available
          };
          // No Firebase path or bucket to store for ID-only attachments
        }
        this.uploadedFiles.push(uploadedFile); // Add to the list of uploaded files (for multi-file support)
      }
    } else {
      // No attachments in RCA data
      this.uploadedFiles = [];
      this.uploadedFile = null;
      this.uploadedFilePath = null;
      this.uploadedFileBucket = null;
    }
    if (rca.regions && this.regions.length > 0) {
      const selectedRegions = rca.regions
        .map((regionName: string) =>
          this.regions.find((r) => r.name === regionName)
        )
        .filter(Boolean); // Filter out any undefined values
      this.rcaForm.get("region")?.patchValue(selectedRegions);
      if (selectedRegions.length > 0) {
        const selectedRegionIds = selectedRegions.map((region: any) => region.id);
        this.countries = this.allCountries.filter((country) =>
          selectedRegionIds.includes(country.region.id)
        );
        this.rcaForm.get("country")?.enable(); // Enable country control
      } else {
        this.countries = [];
        this.rcaForm.get("country")?.disable(); // Disable country control
      }
      this.cdr.detectChanges();
    } else {
      // If no regions are patched, ensure countries are cleared and disabled
      this.rcaForm.get("region")?.patchValue([]);
      this.countries = [];
      this.rcaForm.get("country")?.disable();
      this.cdr.detectChanges();
    }
    if (rca.countries && this.allCountries.length > 0) {
      const selectedCountries = rca.countries
        .map((countryName: string) =>
          this.allCountries.find((c) => c.name === countryName)
        )
        .filter(Boolean);
      this.rcaForm.get("country")?.patchValue(selectedCountries);
    } else {
      // If no countries in RCA data or no matching countries in filtered list, clear it
      this.rcaForm.get("country")?.patchValue([]);
    }
    if (rca.change_types && this.typeOfChanges.length > 0) {
      const selectedChangeTypes = rca.change_types
        .map((typeName: string) =>
          this.typeOfChanges.find((tc) => tc.name === typeName)
        )
        .filter(Boolean);
      this.rcaForm.get("typeOfChange")?.patchValue(selectedChangeTypes);
    }
    if (rca.product_families && this.productFamilies.length > 0) {
      const selectedProductFamilies = rca.product_families
        .map((familyName: string) =>
          this.productFamilies.find((pf) => pf.name === familyName)
        )
        .filter(Boolean);
      this.rcaForm.get("productFamily")?.patchValue(selectedProductFamilies);
    }
    if (
      rca.design_history_docs &&
      this.productDesignHistoryOptions.length > 0
    ) {
      const selectedDesignHistoryDocs = rca.design_history_docs
        .map((docName: string) =>
          this.productDesignHistoryOptions.find((pdh) => pdh.name === docName)
        )
        .filter(Boolean);
      this.rcaForm
        .get("productDesignHistoryDocumentation")
        ?.patchValue(selectedDesignHistoryDocs);

      // Trigger the "Other" field visibility check after patching
      const hasOtherSelected = selectedDesignHistoryDocs.some((doc: ProductDesignHistory) =>
        doc.name?.toLowerCase().includes('other')
      );
      this.showOtherDesignDocsField = hasOtherSelected;
    }

    // Handle other design docs if available from API response
    if (rca.other_design_docs) {
      this.rcaForm.get("otherDesignDocs")?.patchValue(rca.other_design_docs);
    }
    this.rcaPatched.emit();
  }

  private trimFormValues(formValue: any): any {
    const trimmedValue: any = {};
    for (const key in formValue) {
      if (formValue.hasOwnProperty(key)) {
        const value = formValue[key];
        if (typeof value === "string") {
          trimmedValue[key] = value.trim();
        } else if (Array.isArray(value)) {
          trimmedValue[key] = value.map((item) => {
            if (typeof item === "string") return item.trim();
            return item;
          });
        } else {
          trimmedValue[key] = value;
        }
      }
    }
    return trimmedValue;
  }

  private constructPayload(
    isDraft: boolean = false
  ): ManageAssessmentRequestPayload {
    const formValue = this.trimFormValues(this.rcaForm.getRawValue());
    const formatDate = (date: Date | null): any => {
      return date instanceof Date
        ? this.datePipe.transform(date, "yyyy-MM-dd") || null
        : null;
    };
    const usMedDeviceClassId = formValue.usMedDeviceClass?.id
      ? String(formValue.usMedDeviceClass.id)
      : null;
    const euMedDeviceClassId = formValue.euMedDeviceClass?.id
      ? String(formValue.euMedDeviceClass.id)
      : null;
    const usClassificationId = formValue.usClassification?.id
      ? String(formValue.usClassification.id)
      : null;
    const euClassificationId = formValue.euClassification?.id
      ? String(formValue.euClassification.id)
      : null;
    const changeTypeIds = formValue.typeOfChange?.length
      ? formValue.typeOfChange.map((item: any) => item.id)
      : [];
    const countryIds = formValue.country?.length
      ? formValue.country.map((item: any) => item.id)
      : [];
    const designHistoryDocIds = formValue.productDesignHistoryDocumentation
      ?.length
      ? formValue.productDesignHistoryDocumentation.map((item: any) => item.id)
      : [];
    const productFamilyIds = formValue.productFamily?.length
      ? formValue.productFamily.map((item: any) => item.id)
      : [];
    const regionIds = formValue.region?.length
      ? formValue.region.map((item: any) => item.id)
      : [];
    // --- Construct attachment_data similar to SignupComponent ---
    const attachmentData: AttachmentData[] = [];
    if (this.uploadedFiles.length) {
      this.uploadedFiles.forEach(f => {
        if (f.id) {
          attachmentData.push({ id: f.id });
        } else if (f.url && f.path && f.bucket) {
          attachmentData.push({ bucket_name: f.bucket, file_path: f.path, file_name: f.name });
        }
      });
    } else if (this.uploadedFile) { // fallback legacy
      if (this.uploadedFile.id) {
        attachmentData.push({ id: this.uploadedFile.id });
      } else if (this.uploadedFile.url && this.uploadedFilePath && this.uploadedFileBucket) {
        attachmentData.push({ bucket_name: this.uploadedFileBucket, file_path: this.uploadedFilePath, file_name: this.uploadedFile.name });
      }
    }
    // Construct the payload as per ManageAssessmentRequestPayload interface
    const payload: ManageAssessmentRequestPayload = {
      attachment_description: formValue.additionalAttachmentsDescription || null,
      attachment_data: attachmentData,
      catalogue_no: formValue.catalogueNumber || null,
      change_type_ids: changeTypeIds,
      country_ids: countryIds,
      description: formValue.changeDescription || null,
      design_history_doc_ids: designHistoryDocIds,
      other_design_docs: formValue.otherDesignDocs || null, // Include the "Other Design Docs" field
      due_date: formatDate(formValue.dueDate),
      ecn_ern: formValue.ecrnEcnNumber || null,
      eu_class_status: euClassificationId,
      eu_med_class_id: euMedDeviceClassId,
      issue_date: formatDate(formValue.issuedDate),
      planned_timeline: formValue.plannedTimeframeForImplementation || null,
      product_family_ids: productFamilyIds,
      region_ids: regionIds,
      title: formValue.title || null,
      us_class_status: usClassificationId,
      us_med_class_id: usMedDeviceClassId,
    };
    return payload;
  }

  onSubmit(): void {
    // Guard: prevent submit while a file upload is in progress
    if (this.uploading || (this.uploadedFile?.uploading)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Upload In Progress',
        detail: 'Please wait for the file to finish uploading before submitting.',
        life: 4000,
      });
      return;
    }
    this.formSubmitted = true;
    // Ensure all conditional validations are re-evaluated before checking form validity
    this.rcaForm.get('usClassification')?.updateValueAndValidity();
    this.rcaForm.get('usMedDeviceClass')?.updateValueAndValidity();
    this.rcaForm.get('euClassification')?.updateValueAndValidity();
    this.rcaForm.get('euMedDeviceClass')?.updateValueAndValidity();

    if (this.rcaForm.invalid) {
      this.rcaForm.markAllAsTouched();
      this.messageService.add({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please fill all required fields and correct any errors.",
        life: 5000,
      });
      const firstInvalidControl = document.querySelector(".ng-invalid");
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }
    const payload = this.constructPayload(false);
    if (
      this.rcaDataToPatch &&
      Object.keys(this.rcaDataToPatch).length > 0 &&
      this.rcaDataToPatch?.action === "Revise"
    ) {
      this.reviseRca(payload);
    } else if (
      this.rcaDataToPatch &&
      Object.keys(this.rcaDataToPatch).length > 0 &&
      !this.rcaDataToPatch?.row?.version_number?.startsWith("v1")
    ) {
      this.resumeReviseRca(payload);
    } else if (
      this.rcaDataToPatch &&
      Object.keys(this.rcaDataToPatch).length > 0 &&
      this.rcaDataToPatch?.row?.version_number?.startsWith("v1") &&
      (this.rcaDataToPatch?.row?.status === 'Draft' || this.rcaDataToPatch?.row?.display_status === 'Draft')
    ) {
      // existing draft being resumed for first submit
      this.resumeCreateRca(payload);
    } else if (this.rcaDataToPatch && Object.keys(this.rcaDataToPatch).length > 0) {
      // Editing existing RCA (standard update scenario)
      const rcaId = this.rcaDataToPatch?.row?.rca_id;
      this.manageAssessmentApiService.updateManageAssessment(payload, rcaId).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Updated',
            detail: `Regulatory Change Assessment updated successfully!`,
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: `RCA Updated Successfully for assessment ${response?.change_assess_no}`,
            rcaData: response.rca_id,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error('Failed to update RCA:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to update RCA. Please try again.',
            life: 5000,
          });
        }
      });
    } else {
      this.createRca(payload);
    }
  }

  createRca(payload: any) {
    this.manageAssessmentApiService
      .createManageAssessment(payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Regulatory Change Assessment created successfully!`,
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: `New RCA Created Successfully for ${response.change_assess_no}`,
            rcaData: response.rca_id,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to create RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to create RCA. Please try again.",
            life: 5000,
          });
        },
      });
  }

  reviseRca(payload: any) {
    const rcaId = this.rcaDataToPatch?.row?.rca_id || null;
    this.manageAssessmentApiService
      .reviseManageAssessment(payload, rcaId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Regulatory Change Assessment revised successfully!`,
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: `RCA Revised Successfully for an assessment ${response?.change_assess_no}`,
            rcaData: response.rca,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to revise RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to revise RCA. Please try again.",
            life: 5000,
          });
        },
      });
  }

  resumeCreateRca(payload: any) {
    const rcaId = this.rcaDataToPatch?.row?.rca_id || null;
    this.manageAssessmentApiService
      .resumeCreateManageAssessment(payload, rcaId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Regulatory Change Assessment revised successfully!`,
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: `Resume RCA submitted Successfully for an assessment ${response?.change_assess_no}`,
            rcaData: response.rca,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to revise RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to revise RCA. Please try again.",
            life: 5000,
          });
        },
      });
  }

  resumeReviseRca(payload: any) {
    const rcaId = this.rcaDataToPatch?.row?.rca_id || null;
    this.manageAssessmentApiService
      .resumeReviseManageAssessment(payload, rcaId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Regulatory Change Assessment revised successfully!`,
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: `Resume RCA Revised Successfully for an assessment ${response?.change_assess_no}`,
            rcaData: response.rca,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to revise RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to revise RCA. Please try again.",
            life: 5000,
          });
        },
      });
    }
    
  onExport(): void {
    const formData = this.rcaForm.getRawValue();

    const keyValueMapping = {
      additionalAttachmentsDescription: "attachment_description",
      catalogueNumber: "catalogue_no",
      changeDescription: "description",
      ecrnEcnNumber: "ecn_ern",
      euClassification: "eu_class_status",
      euMedDeviceClass: "eu_med_class_id",
      issuedDate: "issue_date",
      dueDate: "due_date",
      otherDesignDocs: "other_design_docs",
      plannedTimeframeForImplementation: "planned_timeline",
      productDesignHistoryDocumentation: "design_history_doc_ids",
      productFamily: "product_family_ids",
      region: "region_ids",
      typeOfChange: "change_type_ids",
      usClassification: "us_class_status",
      usMedDeviceClass: "us_med_class_id",
      country: "country_ids",
    }

    formData.region = formData.region?.map((r: any) => r.name) || [];
    formData.country = formData.country?.map((c: any) => c.name) || [];
    formData.typeOfChange = formData.typeOfChange?.map((t: any) => t.name) || [];
    formData.productFamily = formData.productFamily?.map((p: any) => p.name) || [];
    formData.productDesignHistoryDocumentation = formData.productDesignHistoryDocumentation?.map((d: any) => d.name) || [];
    formData.euClassification = formData.euClassification?.name || "";
    formData.euMedDeviceClass = formData.euMedDeviceClass?.name || "";
    formData.usClassification = formData.usClassification?.name || "";
    formData.usMedDeviceClass = formData.usMedDeviceClass?.name || "";
    formData.issuedDate = formData.issuedDate ? this.datePipe.transform(formData.issuedDate, "yyyy-MM-dd") : "";
    formData.dueDate = formData.dueDate ? this.datePipe.transform(formData.dueDate, "yyyy-MM-dd") : "";

     const mappedData = Object.entries(formData).reduce((acc: Record<string, any>, [key, value]) => {
      const newKey = (keyValueMapping as Record<string, string>)[key] || key; 
      acc[newKey] = value;
      return acc;
    }, {});

    this.manageAssessmentApiService.exportRcaAssessment(mappedData).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: HttpResponse<Blob>) => {
        const blob = response.body;
        if (!blob) {
          console.error("No blob data received for download.");
          alert("Failed to export RCA assessment: No file data.");
          // this.isProcessing = false;
          return;
        }
        let filename = `RCA_Form_Export_${new Date().getTime()}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        // this.isProcessing = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error("Error exporting RCA assessment:", error);
        alert("Failed to export RCA assessment. Please try again later.");
        // this.isProcessing = false;
      },
    });
  }

  onSaveAsDraft(): void {
    // Guard: prevent draft save while a file upload is in progress
    if (this.uploading || (this.uploadedFile?.uploading)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Upload In Progress',
        detail: 'Please wait for the file to finish uploading before saving as draft.',
        life: 4000,
      });
      return;
    }
    const payload = this.constructPayload(true);
    if (this.rcaDataToPatch?.action === "Revise") {
      this.reviseDraft(payload);
    } else if (this.rcaDataToPatch && Object.keys(this.rcaDataToPatch).length > 0) {
      // Editing existing RCA draft save should update instead of creating new draft
      const rcaId = this.rcaDataToPatch?.row?.rca_id;
      this.manageAssessmentApiService.updateManageAssessment(payload, rcaId).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Draft Updated',
            detail: 'RCA draft updated successfully!',
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: `RCA Draft Updated Successfully for assessment ${response?.change_assess_no}`,
            rcaData: response.rca_id,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error('Failed to update RCA draft:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to update RCA draft. Please try again.',
            life: 5000,
          });
        }
      });
    } else {
      this.createDraft(payload);
    }
  }

  createDraft(payload: any) {
    this.manageAssessmentApiService
      .createDraftManageAssessment(payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Draft Saved",
            detail: "Regulatory Change Assessment saved as draft successfully!",
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: `RCA Draft Saved Successfully for the Change Assessment ${response?.change_assess_no}`,
            rcaData: response.rca,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to save RCA as draft:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to save RCA as draft. Please try again.",
            life: 5000,
          });
        },
      });
  }

  reviseDraft(payload: any) {
    const rcaId = this.rcaDataToPatch?.row?.rca_id || null;
    this.manageAssessmentApiService
      .reviseDraftManageAssessment(payload, rcaId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Revise Draft Saved",
            detail:
              "Regulatory Change Assessment revise saved as draft successfully!",
            life: 5000,
          });
          this.rcaCreated.emit({
            isSuccess: true,
            message: "RCA Revised Successfully",
            rcaData: response.rca,
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to save Revise RCA as draft:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to save Revise RCA as draft. Please try again.",
            life: 5000,
          });
        },
      });
  }

  close(): void {
    this.changeDescriptionMaxPreview = false;
    this.resetForm();
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.closeDialog.emit();
  }

  openChangeDescriptionMaxPreview(): void {
    this.changeDescriptionMaxPreview = true;
  }

  closeChangeDescriptionMaxPreview(): void {
    this.changeDescriptionMaxPreview = false;
  }

  private resetForm(): void {
    this.formSubmitted = false;
    this.rcaForm.reset();
    this.rcaForm.get("region")?.setValue([]);
    this.rcaForm.get("usMedDeviceClass")?.setValue(null);
    this.rcaForm.get("productDesignHistoryDocumentation")?.setValue([]);
    this.rcaForm.get("otherDesignDocs")?.setValue(""); // Reset the "Other Design Docs" field
    this.rcaForm.get("country")?.setValue([]);
    this.rcaForm.get("usClassification")?.setValue(null);
    this.rcaForm.get("changeDescription")?.setValue("");
    this.rcaForm.get("ecrnEcnNumber")?.setValue("");
    this.rcaForm.get("euMedDeviceClass")?.setValue(null);
    this.rcaForm.get("plannedTimeframeForImplementation")?.setValue(null);
    this.rcaForm.get("typeOfChange")?.setValue([]);
    this.rcaForm.get("euClassification")?.setValue(null);
    this.rcaForm.get("additionalAttachmentsDescription")?.setValue("");
    this.rcaForm.get("productFamily")?.setValue([]);
    this.rcaForm.get("issuedDate")?.setValue(null);
    this.rcaForm.get("catalogueNumber")?.setValue("");
    this.rcaForm.get("dueDate")?.setValue(null);

    // Clear validators for conditional fields on reset
    this.rcaForm.get('usMedDeviceClass')?.clearValidators();
    this.rcaForm.get('usMedDeviceClass')?.updateValueAndValidity();
    this.rcaForm.get('euMedDeviceClass')?.clearValidators();
    this.rcaForm.get('euMedDeviceClass')?.updateValueAndValidity();

    // Reset the "Other Design Docs" field visibility and validation
    this.showOtherDesignDocsField = false;
    this.rcaForm.get('otherDesignDocs')?.clearValidators();
    this.rcaForm.get('otherDesignDocs')?.updateValueAndValidity();

    this.uploadedFile = null; // Clear uploaded file display
    this.uploadedFiles = []; // Clear the list of uploaded files
    this.uploadedFilePath = null;
    this.uploadedFileBucket = null; // Clear the stored bucket name
    this.uploadError = ""; // Clear any upload errors
    this.uploading = false;
  }

  // --- File Upload Methods ---

  openFileSelector(): void {
    this.fileInput.nativeElement.click();
  }

  async onFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const incoming = Array.from(input.files || []);
    if (!incoming.length) return;
    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/png',
      'image/jpeg',
      'image/jpg'
    ];
    incoming.forEach(async file => {
      if (file.size > maxSize) {
        this.messageService.add({ severity: 'warn', summary: 'Too Large', detail: `${file.name} skipped (size >5MB).`, life: 4000 });
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        this.messageService.add({ severity: 'warn', summary: 'Type Skipped', detail: `${file.name} unsupported format.`, life: 4000 });
        return;
      }
      const tempUrl = URL.createObjectURL(file);
      const entry: UploadedFile = { name: file.name, url: tempUrl, localBlobUrl: tempUrl, rawFile: file, uploading: true, uploaded: false, progress: 0 };
      this.uploadedFiles.push(entry);
      this.cdr.detectChanges();
      try {
        const resourceId = this.rcaDataToPatch?.row?.rca_id || 'new-rca-temp-id';
        const resourceType = 'rca-assessment';
        const downloadUrl = await this.firebaseStorageService.uploadAuthFile(file, resourceType, resourceId);
        const path = this.extractPathFromUrl(downloadUrl);
        const bucket = this.extractBucketFromUrl(downloadUrl);
        entry.url = downloadUrl;
        entry.path = path || undefined;
        entry.bucket = bucket || undefined;
        entry.uploading = false;
        entry.uploaded = true;
        entry.progress = 100;
        if (entry.localBlobUrl) { try { URL.revokeObjectURL(entry.localBlobUrl); } catch {} entry.localBlobUrl = undefined; }
        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: `${file.name} uploaded successfully.`, life: 3000 });
      } catch (e: any) {
        console.error('Upload failed', e);
        entry.uploading = false;
        entry.error = e.message || 'Upload failed';
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: `${file.name}: ${entry.error}`, life: 5000 });
      } finally {
        this.cdr.detectChanges();
      }
    });
    input.value = '';
  }

  async downloadFile(url: string, fileName: string) {
    const isApiDownloadUrl = url.includes("/api/attachments/download/");
    if (isApiDownloadUrl) {
      try {
        const res: any = await firstValueFrom(
          this.apiService.getSignedAttachmentDownloadFromUrl(url)
        );
        if (res && res.download_url) {
          // Use the pre-signed URL for direct download
          const link = document.createElement("a");
          link.href = res.download_url;
          link.download = fileName; // Use the original filename
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.messageService.add({
            severity: "success",
            summary: "Download Started",
            detail: "Your download should begin shortly.",
            life: 3000,
          });
        } else {
          this.messageService.add({
            severity: "error",
            summary: "Download Error",
            detail: "Failed to get a valid download link from the server.",
            life: 5000,
          });
        }
      } catch (err: any) {
        console.error("Failed to get signed attachment URL:", err);
        this.messageService.add({
          severity: "error",
          summary: "Download Failed",
          detail:
            err.error?.message || "Unable to download file. Please try again.",
          life: 5000,
        });
      }
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Multi-file removal (template will pass file reference)
  async removeFile(file: UploadedFile): Promise<void> {
    // multi-file removal
    if (file.id && !file.path) { // existing attachment without path
      this.uploadedFiles = this.uploadedFiles.filter(f => f !== file);
      this.cdr.detectChanges();
      this.messageService.add({ severity: 'success', summary: 'Removed', detail: `${file.name} marked for removal.`, life: 3000 });
      return;
    }
    if (file.path) {
      const isAuthBucket = (file.bucket && file.bucket === environment.firebaseConfig.storageAuthBucket) || (file.url?.includes(environment.firebaseConfig.storageAuthBucket));
      try {
        if (isAuthBucket) {
          await firstValueFrom(this.firebaseStorageService.deleteAuthFile(file.path));
        } else {
          await firstValueFrom(this.firebaseStorageService.deleteFile(file.path));
        }
        if (file.localBlobUrl) { try { URL.revokeObjectURL(file.localBlobUrl); } catch {} }
        this.uploadedFiles = this.uploadedFiles.filter(f => f !== file);
        this.cdr.detectChanges();
        this.messageService.add({ severity: 'success', summary: 'File Removed', detail: `${file.name} removed.`, life: 3000 });
      } catch (err: any) {
        console.error('Failed to delete file', err);
        this.messageService.add({ severity: 'error', summary: 'Removal Failed', detail: err.message || 'Delete failed.', life: 5000 });
      }
      return;
    }
    // fallback legacy single file removal if needed
    if (!this.uploadedFile) {
      return;
    }
    if (this.uploadedFile.id) {
      this.uploadedFile = null;
      this.uploadedFilePath = null;
      this.uploadedFileBucket = null;
      this.cdr.detectChanges();
      this.messageService.add({
        severity: "success",
        summary: "File Removed",
        detail: "Existing attachment marked for removal.",
        life: 3000,
      });
      return;
    }
    const filePathToDelete = this.uploadedFilePath || this.uploadedFile.path;
    if (filePathToDelete) {
      // Determine if file was uploaded to auth bucket (secure) by checking URL or stored bucket name
      const isAuthBucket = (this.uploadedFileBucket && this.uploadedFileBucket === (environment.firebaseConfig.storageAuthBucket))
        || (this.uploadedFile?.url?.includes(environment.firebaseConfig.storageAuthBucket));
      try {
        if (isAuthBucket) {
          await firstValueFrom(this.firebaseStorageService.deleteAuthFile(filePathToDelete));
        } else {
          await firstValueFrom(this.firebaseStorageService.deleteFile(filePathToDelete));
        }
        this.messageService.add({
          severity: "success",
          summary: "File Removed",
          detail: `${this.uploadedFile.name} removed successfully.`,
          life: 3000,
        });
        // Clear local state only after successful deletion from Firebase
        // Revoke any blob URL if still present
        if (this.uploadedFile.url?.startsWith('blob:')) {
          try { URL.revokeObjectURL(this.uploadedFile.url); } catch {}
        }
        this.uploadedFile = null;
        this.uploadedFilePath = null;
        this.uploadedFileBucket = null;
        this.rawSelectedFile = null;
        this.cdr.detectChanges();
        this.uploadError = ""; // Clear any errors
      } catch (error: any) {
        console.error("Failed to delete file from Firebase:", error);
        this.messageService.add({
          severity: "error",
          summary: "Removal Failed",
          detail:
            error.message ||
            "Failed to remove file from storage. Please try again.",
          life: 5000,
        });
      }
    } else {
      // If no path was available (e.g., file was never uploaded or path wasn't stored)
      // just clear the local state.
      this.uploadedFile = null;
      this.uploadedFilePath = null;
      this.uploadedFileBucket = null;
      this.rawSelectedFile = null;
      this.uploadError = "";
      this.messageService.add({
        severity: "info",
        summary: "File Cleared",
        detail:
          "File was not found in storage or path was unavailable, cleared locally.",
        life: 3000,
      });
    }
  }

  // Download either the uploaded Firebase file or the local blob if still uploading
  downloadUploadedOrLocal(file: UploadedFile): void {
    if (file.uploading && file.localBlobUrl) {
      const link = document.createElement('a');
      link.href = file.localBlobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }
    this.downloadFile(file.url, file.name);
  }

  // Legacy wrapper (for any remaining calls without argument)
  private legacyDownloadWrapper(): void {
    if (this.uploadedFile) {
      this.downloadUploadedOrLocal(this.uploadedFile);
    }
  }

  // Helper to extract path from a Firebase Storage download URL
  private extractPathFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split("/");
      const objectPathIndex = pathSegments.indexOf("o");
      if (objectPathIndex > -1 && pathSegments.length > objectPathIndex + 1) {
        const encodedPath = pathSegments.slice(objectPathIndex + 1).join("/");
        return decodeURIComponent(encodedPath);
      }
    } catch (e) {
      console.warn("Could not parse URL to extract path:", url, e);
    }
    return null;
  }

  // Helper to extract bucket name from a Firebase Storage download URL
  private extractBucketFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      // Pathname looks like /v0/b/<bucket_name>/o/...
      const pathSegments = urlObj.pathname.split("/");
      const bucketIndex = pathSegments.indexOf("b");
      if (bucketIndex > -1 && pathSegments.length > bucketIndex + 1) {
        return pathSegments[bucketIndex + 1];
      }
    } catch (e) {
      console.warn("Could not parse URL to extract bucket:", url, e);
    }
    return null;
  }

  // Generate tooltip text based on file status
  getFileTooltipText(): string {
    if (!this.uploadedFile) {
      return "";
    }

    const fileName = this.uploadedFile.name;
    const status = this.uploadedFile.status;

    if (status === "pending") {
      return `${fileName} - Status: Pending`;
    } else {
      // For "passed" status or any other status, show only filename
      return fileName;
    }
  }

  // Helper for template to check if any file still uploading
  hasUploadingFiles(): boolean {
    return this.uploadedFiles?.some(f => f.uploading) || false;
  }


  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}